import { useCallback, useEffect, useRef, useState } from 'react';
import type { SkuT } from '@lambda/apis/src/catalog-v2/types';
import type { CompatiblePageT } from '@/lib/catalog/compatible-accessories';

const NOTHING_LOADED: CompatiblePageT = { items: [], nextOffset: 0 };

/**
 * The products compatible with any of `productIds` ("More accessories"), starting from
 * `initial` (a first page rendered server-side, or nothing yet to load from
 * the start) and read a page at a time as `loadMore` is called. Starts over
 * whenever the ids change.
 */
const useCompatibleAccessories = (
  productIds: string[],
  initial: CompatiblePageT = NOTHING_LOADED,
) => {
  const key = productIds.join(',');
  const [items, setItems] = useState<SkuT[]>(initial.items);
  const [nextOffset, setNextOffset] = useState(initial.nextOffset);
  const [pagedKey, setPagedKey] = useState(key);
  // Bumped on every start-over: a request belongs to the run it was made in,
  // so one made before (even for the same ids, after A → B → A) neither
  // lands in the new list, shows as loading, nor blocks the new run's.
  const [run, setRun] = useState(0);
  const [loadingRun, setLoadingRun] = useState<number | null>(null);
  const inFlightRun = useRef<number | null>(null);
  const currentRun = useRef(run);

  useEffect(() => {
    currentRun.current = run;
  }, [run]);

  // Start over as soon as the ids change, during render rather than in an
  // effect: React re-renders before committing, so no child (the infinite
  // scroll trigger, which calls `loadMore` the moment it's recreated) ever
  // sees the previous ids' offset.
  if (pagedKey !== key) {
    setPagedKey(key);
    setRun((previous) => previous + 1);
    setItems([]);
    setNextOffset(productIds.length ? 0 : null);
  }

  const loadMore = useCallback(async () => {
    if (
      nextOffset === null ||
      inFlightRun.current === run ||
      productIds.length === 0
    ) {
      return;
    }

    const requestRun = run;

    inFlightRun.current = requestRun;
    setLoadingRun(requestRun);

    try {
      const params = new URLSearchParams(
        productIds.map((id) => ['productId', id]),
      );

      params.set('offset', String(nextOffset));

      const res = await fetch(
        `/api/catalog-v2/products/compatibility?${params.toString()}`,
      );

      if (!res.ok) {
        throw new Error('Failed to fetch compatible products');
      }

      const page: CompatiblePageT = await res.json();

      if (currentRun.current !== requestRun) {
        return;
      }

      setItems((previous) => {
        const seen = new Set(previous.map((sku) => sku.id));

        return [...previous, ...page.items.filter((sku) => !seen.has(sku.id))];
      });
      setNextOffset(page.nextOffset);
    } catch {
      // Stop paging rather than retry in a loop; what's shown stays.
      if (currentRun.current === requestRun) {
        setNextOffset(null);
      }
    } finally {
      if (inFlightRun.current === requestRun) {
        inFlightRun.current = null;
      }

      setLoadingRun((loadingFor) =>
        loadingFor === requestRun ? null : loadingFor,
      );
    }
    // `run` changes whenever the ids do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, nextOffset]);

  return {
    items,
    loadMore,
    hasMore: nextOffset !== null && productIds.length > 0,
    loading: loadingRun === run,
  };
};

export default useCompatibleAccessories;
